import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Send,
  Loader2,
  AlertCircle,
} from 'lucide-react';

/* ─── Types ─── */
interface ReviewData {
  id: string;
  sellerId: string;
  buyerId: string;
  listingId: string;
  listingTitle: string;
  rating: number;
  comment: string;
  communicationRating: number | null;
  itemConditionRating: number | null;
  deliveryRating: number | null;
  status: string;
  helpfulCount: number;
  unhelpfulCount: number;
  verifiedPurchase: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RatingStats {
  sellerId: string;
  totalReviews: number;
  averageRating: number;
  averageCommunicationRating: number;
  averageItemConditionRating: number;
  averageDeliveryRating: number;
  fiveStarCount: number;
  fourStarCount: number;
  threeStarCount: number;
  twoStarCount: number;
  oneStarCount: number;
  verifiedPurchaseCount: number;
}

interface Props {
  sellerId: string;
  listingId: string;
  listingTitle: string;
}

/* ─── Helpers ─── */
function timeAgo(dateString: string) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (isNaN(seconds) || seconds < 0) return 'some time ago';
  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) return `${interval} yr${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return `${interval} mo${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return `${interval} day${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return `${interval} hr${interval === 1 ? '' : 's'} ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return `${interval} min${interval === 1 ? '' : 's'} ago`;
  return 'just now';
}

/* ─── Star Rating Input ─── */
const StarRating: React.FC<{
  value: number;
  onChange: (v: number) => void;
  size?: number;
  label?: string;
  required?: boolean;
}> = ({ value, onChange, size = 28, label, required }) => {
  const [hover, setHover] = useState(0);
  const labels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  return (
    <div className="star-rating-input">
      {label && (
        <span className="star-rating-input__label">
          {label}
          {required && <span className="star-rating-input__required">*</span>}
        </span>
      )}
      <div className="star-rating-input__stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star-rating-input__btn ${
              star <= (hover || value) ? 'star-rating-input__btn--active' : ''
            }`}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(star)}
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            <Star
              style={{ width: size, height: size }}
              className={star <= (hover || value) ? 'star-filled' : 'star-empty'}
            />
          </button>
        ))}
        {(hover || value) > 0 && (
          <span className="star-rating-input__text">{labels[hover || value]}</span>
        )}
      </div>
    </div>
  );
};

/* ─── Star Display (read-only) ─── */
const StarDisplay: React.FC<{ rating: number; size?: number }> = ({ rating, size = 16 }) => (
  <span className="star-display">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        style={{ width: size, height: size }}
        className={star <= Math.round(rating) ? 'star-filled' : 'star-empty'}
      />
    ))}
  </span>
);

/* ─── Rating Bar ─── */
const RatingBar: React.FC<{ label: string; count: number; total: number }> = ({
  label,
  count,
  total,
}) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="rating-bar">
      <span className="rating-bar__label">{label}</span>
      <div className="rating-bar__track">
        <div className="rating-bar__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="rating-bar__count">{count}</span>
    </div>
  );
};

/* ─── Main Component ─── */
const SellerReviews: React.FC<Props> = ({ sellerId, listingId, listingTitle }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Data
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [totalReviewPages, setTotalReviewPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [communicationRating, setCommunicationRating] = useState(0);
  const [itemConditionRating, setItemConditionRating] = useState(0);
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showSubRatings, setShowSubRatings] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!sellerId) return;
    setLoadingStats(true);
    try {
      const data = await apiClient.get(`/api/reviews/seller/${sellerId}/stats`);
      setStats(data);
    } catch {
      // Stats not available yet
      setStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [sellerId]);

  const fetchReviews = useCallback(
    async (page = 0) => {
      if (!sellerId) return;
      setLoadingReviews(true);
      try {
        const data = await apiClient.get(
          `/api/reviews/seller/${sellerId}?page=${page}&size=5&sort=createdAt,desc`
        );
        const items = data?.content ?? data?.items ?? (Array.isArray(data) ? data : []);
        setReviews(items);
        setTotalReviewPages(data?.totalPages ?? 1);
        setCurrentPage(page);
      } catch {
        setReviews([]);
      } finally {
        setLoadingReviews(false);
      }
    },
    [sellerId]
  );

  useEffect(() => {
    fetchStats();
    fetchReviews(0);
  }, [fetchStats, fetchReviews]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      setSubmitError('Please select a rating');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      const payload: any = {
        sellerId,
        listingId,
        listingTitle,
        rating,
        comment: comment.trim() || null,
      };
      if (communicationRating > 0) payload.communicationRating = communicationRating;
      if (itemConditionRating > 0) payload.itemConditionRating = itemConditionRating;
      if (deliveryRating > 0) payload.deliveryRating = deliveryRating;

      await apiClient.post('/api/reviews', payload);
      setSubmitSuccess(true);
      setRating(0);
      setComment('');
      setCommunicationRating(0);
      setItemConditionRating(0);
      setDeliveryRating(0);
      setShowForm(false);
      setShowSubRatings(false);
      // Refresh data
      fetchStats();
      fetchReviews(0);
    } catch (err: any) {
      const msg =
        err?.message || err?.errors?.rating?.[0] || 'Failed to submit review. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (reviewId: string, type: 'helpful' | 'unhelpful') => {
    try {
      await apiClient.post(`/api/reviews/${reviewId}/${type}`, {});
      fetchReviews(currentPage);
    } catch {
      // silent
    }
  };

  const avgRating = stats?.averageRating ?? 0;
  const totalReviews = stats?.totalReviews ?? 0;

  return (
    <section className="seller-reviews">
      {/* ─── Stats Summary ─── */}
      <div className="seller-reviews__header">
        <h3 className="seller-reviews__title">
          <MessageSquare className="seller-reviews__title-icon" />
          Ratings & Reviews
        </h3>
      </div>

      {loadingStats ? (
        <div className="seller-reviews__loader">
          <Loader2 className="animate-spin" style={{ width: 20, height: 20 }} />
        </div>
      ) : totalReviews > 0 ? (
        <div className="seller-reviews__stats">
          <div className="seller-reviews__avg">
            <span className="seller-reviews__avg-number">{avgRating.toFixed(1)}</span>
            <StarDisplay rating={avgRating} size={20} />
            <span className="seller-reviews__avg-count">
              {totalReviews} review{totalReviews !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="seller-reviews__bars">
            <RatingBar label="5★" count={stats?.fiveStarCount ?? 0} total={totalReviews} />
            <RatingBar label="4★" count={stats?.fourStarCount ?? 0} total={totalReviews} />
            <RatingBar label="3★" count={stats?.threeStarCount ?? 0} total={totalReviews} />
            <RatingBar label="2★" count={stats?.twoStarCount ?? 0} total={totalReviews} />
            <RatingBar label="1★" count={stats?.oneStarCount ?? 0} total={totalReviews} />
          </div>
          {(stats?.averageCommunicationRating || stats?.averageItemConditionRating || stats?.averageDeliveryRating) && (
            <div className="seller-reviews__sub-avgs">
              {stats.averageCommunicationRating > 0 && (
                <div className="seller-reviews__sub-avg-item">
                  <span>Communication</span>
                  <span className="seller-reviews__sub-avg-value">
                    <Star className="star-filled" style={{ width: 12, height: 12 }} />
                    {stats.averageCommunicationRating.toFixed(1)}
                  </span>
                </div>
              )}
              {stats.averageItemConditionRating > 0 && (
                <div className="seller-reviews__sub-avg-item">
                  <span>Item Accuracy</span>
                  <span className="seller-reviews__sub-avg-value">
                    <Star className="star-filled" style={{ width: 12, height: 12 }} />
                    {stats.averageItemConditionRating.toFixed(1)}
                  </span>
                </div>
              )}
              {stats.averageDeliveryRating > 0 && (
                <div className="seller-reviews__sub-avg-item">
                  <span>Delivery</span>
                  <span className="seller-reviews__sub-avg-value">
                    <Star className="star-filled" style={{ width: 12, height: 12 }} />
                    {stats.averageDeliveryRating.toFixed(1)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="seller-reviews__empty-stats">
          <Star style={{ width: 20, height: 20, opacity: 0.4 }} />
          <span>No reviews yet. Be the first to review!</span>
        </div>
      )}

      {/* ─── Write Review Button / Form ─── */}
      {!showForm && !submitSuccess && (
        <button
          className="seller-reviews__write-btn"
          onClick={() => {
            if (!isAuthenticated) {
              navigate('/login', { state: { from: `/listings/${listingId}` } });
              return;
            }
            setShowForm(true);
            setSubmitError('');
          }}
        >
          <Star style={{ width: 16, height: 16 }} />
          Write a Review
        </button>
      )}

      {submitSuccess && (
        <div className="seller-reviews__success">
          <ShieldCheck style={{ width: 18, height: 18 }} />
          Thank you! Your review has been submitted successfully.
        </div>
      )}

      {showForm && (
        <form className="seller-reviews__form" onSubmit={handleSubmit}>
          <div className="seller-reviews__form-header">
            <h4>Rate this seller</h4>
            <button
              type="button"
              className="seller-reviews__form-close"
              onClick={() => {
                setShowForm(false);
                setSubmitError('');
              }}
            >
              ✕
            </button>
          </div>

          <StarRating
            value={rating}
            onChange={setRating}
            size={32}
            label="Overall Rating"
            required
          />

          <button
            type="button"
            className="seller-reviews__toggle-sub"
            onClick={() => setShowSubRatings(!showSubRatings)}
          >
            {showSubRatings ? <ChevronUp style={{ width: 16, height: 16 }} /> : <ChevronDown style={{ width: 16, height: 16 }} />}
            {showSubRatings ? 'Hide' : 'Add'} detailed ratings
          </button>

          {showSubRatings && (
            <div className="seller-reviews__sub-ratings">
              <StarRating
                value={communicationRating}
                onChange={setCommunicationRating}
                size={22}
                label="Communication"
              />
              <StarRating
                value={itemConditionRating}
                onChange={setItemConditionRating}
                size={22}
                label="Item Condition"
              />
              <StarRating
                value={deliveryRating}
                onChange={setDeliveryRating}
                size={22}
                label="Delivery / Pickup"
              />
            </div>
          )}

          <div className="seller-reviews__comment-wrap">
            <label htmlFor="review-comment" className="seller-reviews__comment-label">
              Your Review <span className="seller-reviews__optional">(optional)</span>
            </label>
            <textarea
              id="review-comment"
              className="seller-reviews__textarea"
              placeholder="Share your experience with this seller..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
              rows={4}
            />
            <span className="seller-reviews__char-count">{comment.length}/1000</span>
          </div>

          {submitError && (
            <div className="seller-reviews__error">
              <AlertCircle style={{ width: 16, height: 16 }} />
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="seller-reviews__submit-btn"
            disabled={submitting || rating === 0}
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" style={{ width: 16, height: 16 }} />
                Submitting…
              </>
            ) : (
              <>
                <Send style={{ width: 16, height: 16 }} />
                Submit Review
              </>
            )}
          </button>
        </form>
      )}

      {/* ─── Reviews List ─── */}
      {loadingReviews ? (
        <div className="seller-reviews__loader">
          <Loader2 className="animate-spin" style={{ width: 20, height: 20 }} />
        </div>
      ) : reviews.length > 0 ? (
        <div className="seller-reviews__list">
          {reviews.map((review) => (
            <div key={review.id} className="seller-reviews__item">
              <div className="seller-reviews__item-header">
                <div className="seller-reviews__item-avatar">
                  {review.buyerId?.[0]?.toUpperCase() || 'B'}
                </div>
                <div className="seller-reviews__item-meta">
                  <div className="seller-reviews__item-stars">
                    <StarDisplay rating={review.rating} size={14} />
                    <span className="seller-reviews__item-rating-text">{review.rating}/5</span>
                  </div>
                  <span className="seller-reviews__item-time">{timeAgo(review.createdAt)}</span>
                </div>
                {review.verifiedPurchase && (
                  <span className="seller-reviews__verified-badge">
                    <ShieldCheck style={{ width: 12, height: 12 }} /> Verified
                  </span>
                )}
              </div>

              {review.listingTitle && (
                <p className="seller-reviews__item-listing">For: {review.listingTitle}</p>
              )}

              {review.comment && (
                <p className="seller-reviews__item-comment">{review.comment}</p>
              )}

              {(review.communicationRating || review.itemConditionRating || review.deliveryRating) && (
                <div className="seller-reviews__item-sub-ratings">
                  {review.communicationRating && (
                    <span>Communication: <strong>{review.communicationRating}/5</strong></span>
                  )}
                  {review.itemConditionRating && (
                    <span>Item: <strong>{review.itemConditionRating}/5</strong></span>
                  )}
                  {review.deliveryRating && (
                    <span>Delivery: <strong>{review.deliveryRating}/5</strong></span>
                  )}
                </div>
              )}

              <div className="seller-reviews__item-actions">
                <button
                  className="seller-reviews__helpful-btn"
                  onClick={() => handleHelpful(review.id, 'helpful')}
                >
                  <ThumbsUp style={{ width: 13, height: 13 }} />
                  Helpful{review.helpfulCount > 0 ? ` (${review.helpfulCount})` : ''}
                </button>
                <button
                  className="seller-reviews__helpful-btn"
                  onClick={() => handleHelpful(review.id, 'unhelpful')}
                >
                  <ThumbsDown style={{ width: 13, height: 13 }} />
                  {review.unhelpfulCount > 0 ? ` (${review.unhelpfulCount})` : ''}
                </button>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalReviewPages > 1 && (
            <div className="seller-reviews__pagination">
              <button
                disabled={currentPage === 0}
                onClick={() => fetchReviews(currentPage - 1)}
                className="seller-reviews__page-btn"
              >
                ← Prev
              </button>
              <span className="seller-reviews__page-info">
                Page {currentPage + 1} of {totalReviewPages}
              </span>
              <button
                disabled={currentPage >= totalReviewPages - 1}
                onClick={() => fetchReviews(currentPage + 1)}
                className="seller-reviews__page-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      ) : (
        !loadingStats && (
          <p className="seller-reviews__no-reviews">
            No reviews to display yet.
          </p>
        )
      )}
    </section>
  );
};

export default SellerReviews;
